import * as React from 'react';
import { useSnackbar } from 'notistack';
import axios from '../../utils/axios';
import { Num } from './index';
import Modal, { BotaoSecundario, Campo, classesEntrada } from './Modal';
import { cn } from '../../lib/utils';
import { AutoCompleteUnico, buscarPaginado } from './AutoComplete';

/* ============================================================================
   Mudar o grupo de um dispositivo - visual novo.

   Substitui pages/dashboard/dispositivos/components/modal/modal-mudar-grupo.tsx.

   ISTO TOCA APARELHO EM CAMPO: mudar de grupo troca a POLITICA aplicada ao
   aparelho. O que estava liberado pode passar a ser bloqueado no minuto seguinte.
   Por isso tem confirmacao com o de-para escrito.

   Contrato, o mesmo de antes:
     PATCH /dispositivos/mudarGrupo { entidade, oldEntidade, deviceId }
     GET   /entidades/paginado?...&remover=<grupo atual>&minhasregionais=true&somenteComPoliticas=true

   ---------------------------------------------------------------------------
   DOIS DEFEITOS DA VERSAO ANTIGA, corrigidos aqui:

   1. O HISTORICO NUNCA REGISTROU O GRUPO DE ORIGEM.
      O backend monta a trilha lendo `body.oldEntidade.entidade.nome`
      (dispositivos.service.ts:1734) - repare no `entidade` DENTRO de
      `oldEntidade`. O modal antigo enviava `oldEntidade: device.entidade`, um
      nivel a menos, entao aquele caminho dava `undefined` e o historico gravava
      `"antigo": " - "` SEMPRE. Pior: chamado a partir da LISTAGEM, `device` nem
      tem `entidade` (a consulta devolve `grupoNome`/`grupoCodigo` achatados),
      entao era `undefined` de qualquer jeito.
      Aqui o corpo e montado no formato que o backend le, a partir do grupo atual
      - com isso a trilha passa a dizer de onde o aparelho saiu.

   2. `remover` NAO EXCLUIA O GRUPO ATUAL da lista de destinos.
      O antigo usava `device.entidade?.codigo`, que na listagem e `undefined`,
      entao o grupo atual aparecia como opcao. Escolher ele resulta em erro do
      backend ("O dispositivo ja pertence a esse grupo"). Agora usa o codigo que
      a listagem realmente devolve.

   E o `push(...)` para `/dispositivos/${response?.data?.entidade?.codigo}` que
   vinha depois de salvar saiu: esse caminho tambem nao existe no retorno.
   ============================================================================ */

type Grupo = { codigo: number; nome: string };

type Device = {
  deviceId?: string;
  nome?: string;
  grupoNome?: string;
  grupoCodigo?: number;
  /** Presente quando o modal e aberto da tela de DETALHE, nao da listagem. */
  entidade?: { codigo?: number; nome?: string } | null;
};

export default function ModalMudarGrupo({
  aberto,
  device,
  onFechar,
  onSalvo,
}: {
  aberto: boolean;
  device: Device | null;
  onFechar: () => void;
  onSalvo?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();

  const [destino, setDestino] = React.useState<Grupo | null>(null);
  const [confirmando, setConfirmando] = React.useState(false);
  const [salvando, setSalvando] = React.useState(false);

  // A listagem devolve o grupo achatado (`grupoCodigo`/`grupoNome`); a tela de
  // detalhe devolve o objeto `entidade`. Aceita os dois.
  const grupoAtual = React.useMemo(
    () => ({
      codigo: device?.grupoCodigo ?? device?.entidade?.codigo,
      nome: device?.grupoNome ?? device?.entidade?.nome,
    }),
    [device]
  );

  React.useEffect(() => {
    if (!aberto) return;
    setDestino(null);
    setConfirmando(false);
  }, [aberto, device?.deviceId]);

  /* Mesmo endpoint e filtros de antes, mas so ao clicar na lupa e 20 por
     pagina (21/10/2026) - antes eram 100 grupos ao abrir e mais 100 por pausa
     na digitacao. `remover` tira o grupo atual da lista. */
  const buscarGrupos = React.useMemo(
    () =>
      buscarPaginado(
        (termo, pagina, linhas) =>
          `/entidades/paginado?page=${pagina}&rows=${linhas}&campo=nome&valor=${encodeURIComponent(termo)}` +
          `&direcao=asc&ordenar=nome&status=1&remover=${grupoAtual.codigo ?? ''}` +
          `&minhasregionais=true&somenteComPoliticas=true`,
        (g) => ({ codigo: g.codigo, rotulo: g.nome, dado: g })
      ),
    [grupoAtual.codigo]
  );

  async function salvar() {
    if (!device?.deviceId || !destino || salvando) return;
    setSalvando(true);
    try {
      await axios.patch('/dispositivos/mudarGrupo', {
        entidade: destino,
        // Formato que o backend REALMENTE le: `oldEntidade.entidade.nome`.
        oldEntidade: { entidade: { codigo: grupoAtual.codigo, nome: grupoAtual.nome } },
        deviceId: device.deviceId,
      });

      // A troca de grupo grava na hora; a politica nova vale quando o aparelho sincronizar.
      enqueueSnackbar('Grupo alterado. A política nova vale quando o aparelho sincronizar.', { variant: 'success' });
      setConfirmando(false);
      onSalvo?.();
      onFechar();
    } catch (err: any) {
      const e = err?.response?.data;
      const msg = e?.errors?.length ? e.errors[0]?.message : e?.message;
      enqueueSnackbar(msg || 'Ops ocorreu um erro no sistema.', {
        variant: 'error',
        style: { whiteSpace: 'pre-line' },
      });
    } finally {
      setSalvando(false);
    }
  }

  return (
    <>
      <Modal
        aberto={aberto && !confirmando}
        titulo="Mudar grupo"
        descricao={device ? `${device.nome ?? ''} · ${device.deviceId ?? ''}` : undefined}
        icone="🗂️"
        largura="max-w-[560px]"
        onFechar={onFechar}
        rodape={
          <>
            <BotaoSecundario onClick={onFechar} disabled={salvando}>
              Cancelar
            </BotaoSecundario>
            <button
              type="button"
              onClick={() => setConfirmando(true)}
              disabled={!destino || salvando}
              className="h-9 rounded-lg bg-warn px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              Mudar grupo
            </button>
          </>
        }
      >
        <div className="rounded-lg border border-line bg-surface-2 p-3">
          <span className="block text-[11px] font-bold uppercase tracking-[0.07em] text-faint">Grupo atual</span>
          <span className="block text-[12.5px] font-semibold">{grupoAtual.nome || '—'}</span>
        </div>

        <Campo bloco
          rotulo="Novo grupo"
          obrigatorio
          dica="Só grupos das suas regionais que tenham política. O grupo atual não aparece na lista."
        >
          <AutoCompleteUnico
            selecionado={destino ? { codigo: destino.codigo, rotulo: destino.nome } : null}
            /* Guarda o REGISTRO do servidor (`dado`), nao so codigo e nome: e ele
               que vai como `entidade` no PATCH, como antes. */
            aoEscolher={(o) => setDestino(o ? o.dado : null)}
            buscar={buscarGrupos}
            titulo="Selecionar novo grupo"
            placeholder="Nome do grupo (Enter ou lupa)"
            vazio="Nenhum grupo com política disponível."
          />
        </Campo>

        <div className="rounded-lg border border-warn bg-warn-soft p-3 text-[12px] text-warn">
          Mudar de grupo troca a <strong>política aplicada ao aparelho</strong>. O que estava liberado pode
          passar a ser bloqueado, e o contrário também.
        </div>
      </Modal>

      <Modal
        aberto={confirmando}
        titulo="Confirmar mudança de grupo?"
        descricao="A política do novo grupo passa a valer no aparelho."
        icone="!"
        largura="max-w-[500px]"
        onFechar={() => setConfirmando(false)}
        rodape={
          <>
            <BotaoSecundario onClick={() => setConfirmando(false)} disabled={salvando}>
              Voltar
            </BotaoSecundario>
            <button
              type="button"
              onClick={salvar}
              disabled={salvando}
              className="h-9 rounded-lg bg-warn px-4 text-[12.5px] font-bold text-white disabled:opacity-40"
            >
              {salvando ? 'Mudando...' : 'Confirmar'}
            </button>
          </>
        }
      >
        <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px]">
          <span className="block font-semibold">{device?.nome || '—'}</span>
          <Num className="block text-[11px] text-muted-foreground">{device?.deviceId || '—'}</Num>

          {/* O de-para escrito: e o que evita mudar o aparelho para o grupo
              errado quando dois nomes se parecem. */}
          <div className="mt-3 flex items-center gap-2 text-[12px]">
            <span className="rounded bg-soft px-2 py-1 text-muted-foreground">{grupoAtual.nome || '—'}</span>
            <span className="text-faint">→</span>
            <span className="rounded bg-primary/10 px-2 py-1 font-semibold text-ink">{destino?.nome || '—'}</span>
          </div>
        </div>
      </Modal>
    </>
  );
}
